import { useState } from 'react';
import { Calendar } from 'lucide-react';
import { cn } from '@/lib/cn';
import { fechaCorta } from '@/lib/formato-tienda';
import { PERIODOS, rangoDe, type PeriodoId } from './periodo';

/**
 * LA BARRA DEL PERÍODO. El cálculo de los rangos vive en `periodo.ts`: este
 * archivo solo exporta el componente, que es lo que necesita el fast refresh
 * de Vite para recargarlo sin tirar abajo la pantalla entera.
 */
export function FilterBarPeriodo({
  periodo,
  onCambiar,
  personalizado,
  onPersonalizado,
  className,
}: {
  periodo: PeriodoId;
  onCambiar: (p: PeriodoId) => void;
  personalizado?: { desde?: string; hasta?: string };
  onPersonalizado?: (v: { desde?: string; hasta?: string }) => void;
  className?: string;
}) {
  const [abierto, setAbierto] = useState(periodo === 'personalizado');
  const rango = rangoDe(periodo, personalizado);

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex flex-wrap items-center gap-1">
        {PERIODOS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => {
              setAbierto(false);
              onCambiar(p.id);
            }}
            className={cn(
              'rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
              periodo === p.id
                ? 'bg-brand-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700',
            )}
          >
            {p.etiqueta}
          </button>
        ))}

        {onPersonalizado && (
          <button
            type="button"
            onClick={() => {
              setAbierto((v) => !v);
              onCambiar('personalizado');
            }}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
              periodo === 'personalizado'
                ? 'bg-brand-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300',
            )}
          >
            <Calendar className="h-3.5 w-3.5" />
            Personalizado
          </button>
        )}
      </div>

      {abierto && onPersonalizado && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <label className="flex items-center gap-1.5">
            <span className="text-slate-500">Desde</span>
            <input
              type="date"
              value={personalizado?.desde ?? ''}
              onChange={(e) => onPersonalizado({ ...personalizado, desde: e.target.value })}
              className="h-8 rounded-md border border-surface-border bg-surface px-2"
            />
          </label>
          <label className="flex items-center gap-1.5">
            <span className="text-slate-500">Hasta</span>
            <input
              type="date"
              value={personalizado?.hasta ?? ''}
              onChange={(e) => onPersonalizado({ ...personalizado, hasta: e.target.value })}
              className="h-8 rounded-md border border-surface-border bg-surface px-2"
            />
          </label>
        </div>
      )}

      <p className="text-xs text-slate-500">
        {fechaCorta(rango.actual.desde)} — {fechaCorta(new Date(rango.actual.hasta.getTime() - 1))}
        <span className="mx-1.5 text-slate-300">·</span>
        se compara contra {rango.etiquetaAnterior}
      </p>
    </div>
  );
}
